import ClientFormConfig from "@components/forms/ClientForm/form";
import { ClientFormData } from "@/formTypes/ClientFormDataType";
import { buildImportFieldsFromForm } from "./core/fieldsFromForm";
import { ImportField } from "./core/types";

/** Como hay que normalizar el valor crudo del archivo antes de guardarlo. */
export type ImportValueKind =
    | "text"
    | "code"
    | "identification"
    | "phone"
    | "phoneCountryCode"
    | "email"
    | "integer"
    | "boolean"
    | "clientType"
    | "activityCode"
    | "zoneName";

export type ClientImportField = ImportField & {
    name: keyof ClientFormData & string;
    kind: ImportValueKind;
};

/**
 * Atributos que no se ofrecen como destino de una columna:
 * - `tipo_identificacion` se infiere siempre del numero de identificacion.
 * - `priceId` y `salemanId` son selects por id: no hay forma de resolverlos desde texto
 *   (a diferencia de la zona, que se busca y se crea por nombre).
 * - `is_default` marca el cliente por defecto del tenant; solo puede haber uno.
 */
const EXCLUDED_FIELDS = new Set<string>(["tipo_identificacion", "priceId", "salemanId", "is_default"]);

/** Etiquetas propias donde la del formulario no describe bien lo que se va a importar. */
const LABEL_OVERRIDES: Record<string, string> = {
    zoneId: "Zona de Cliente (por nombre)",
    activitycode: "Codigo Actividad Economica",
};

const FIELD_KINDS: Record<string, ImportValueKind> = {
    codigo: "code",
    nombre: "text",
    nombre_comercial: "text",
    identificacion: "identification",
    telefono1: "phone",
    telefono2: "phone",
    telefono1_cd: "phoneCountryCode",
    telefono2_cd: "phoneCountryCode",
    email: "email",
    tipo: "clientType",
    plazo: "integer",
    descuento: "integer",
    credit_limit: "integer",
    active: "boolean",
    with_user_info: "boolean",
    activitycode: "activityCode",
    zoneId: "zoneName",
};

/**
 * Campos que existen en el modelo `Client` pero no en el formulario, porque los inputs
 * de telefono manejan el codigo de pais internamente. Se agregan a mano para poder
 * mapearlos, y tienen un valor por defecto cuando la columna no viene en el archivo.
 */
const EXTRA_FIELDS: ClientImportField[] = [
    { name: "telefono1_cd", label: "Codigo de pais Telefono 1", kind: "phoneCountryCode" },
    { name: "telefono2_cd", label: "Codigo de pais Telefono 2", kind: "phoneCountryCode" },
];

/**
 * Atributos del cliente que se pueden mapear, con la etiqueta que ya usa el formulario.
 *
 * Leerlas del formulario en vez de repetirlas evita que el importador y la pantalla de
 * edicion se desincronicen cuando se renombra un campo.
 */
export const buildClientImportFields = (): ClientImportField[] =>
    buildImportFieldsFromForm(ClientFormConfig, {
        excluded: EXCLUDED_FIELDS,
        labelOverrides: LABEL_OVERRIDES,
        kinds: FIELD_KINDS,
        extraFields: EXTRA_FIELDS,
    }) as ClientImportField[];

export const CLIENT_IMPORT_FIELDS: ClientImportField[] = buildClientImportFields();

export const findClientImportField = (name?: string | null): ClientImportField | undefined =>
    CLIENT_IMPORT_FIELDS.find((field) => field.name === name);

/**
 * Atributos que el usuario esta obligado a mapear para poder continuar.
 * El codigo no esta aqui: si no se mapea se autogenera consecutivo.
 */
export const REQUIRED_IMPORT_FIELDS: Array<ClientImportField["name"]> = ["nombre", "identificacion"];
